import {
  BooleanInput,
  FormDataConsumer,
  ImageInput,
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
  useGetList,
  ReferenceArrayInput,
  AutocompleteInput,
  Labeled,
  SelectArrayInput,
} from 'react-admin';
import { useMemo } from 'react';
import { RichTextInputToolbar, ListButtons, LinkButtons, ClearButtons } from 'ra-input-rich-text';
import { FormControlLabel, FormGroup, Switch, ToggleButtonGroup } from '@mui/material';
import { FieldTitle, useRecordContext, useTranslate } from 'ra-core';
import AgendaResource from './AgendaResource';
import { ServiceImageField } from '../../components/Field/ServiceImageField';
import { RichTextInput } from '../../components/RichTextJsonInput';
import { VideoButton } from '../../components/VideoButton';
import { CustomFormatButtons } from '../../components/FormatButtons';
import { GroupedRegionDepartmentSelectArrayInput } from '../../components/Input/GroupedRegionDepartmentSelectArrayInput';
import { LevelSelect } from '../../components/LevelSelect';
import { HorizontalRuleButton } from '../../components/HorizontalRuleButton';
import { usePermissionGuard } from '../../lib/permissions/usePermissionGuard';
import NewsResource from '../News/NewsResource';
import { ImageButton } from '../../components/ImageButton';
import { LinkContentButton } from '../../components/LinkContentButton';
import { LinkFileButton } from '../../components/LinkFileButton';
import { transformMediaUrlInput } from '../../lib/media/makeUrl';
import { isUrlValid } from '../../lib/media/isValidUrl';
import { DateTimePicker } from '../../components/Input/DateTimePickerInput';
import SideBySideView from '../../components/SideBySide/SideBySideView';
import SharableLinkButton from '../../components/SharableLinkButton/generateSharableLink';


let urlValid = false;

const urlExists = (url: string) => {
  if (url)
    return urlValid ? undefined : 'lto.generic.url_notfound';
  else
    return undefined;
}

const SectorOrTopicRequired = (value, allValues, props) => {
  if (props.source === 'topicIds' && allValues.topicIds && allValues.topicIds.length > 2) {
    return 'lto.generic.max_two_topics_allowed'
  }

  if (props.source === 'sectorIds' && allValues.sectorIds && allValues.sectorIds.length > 5) {
    return 'lto.generic.max_five_sectors_allowed'
  }

  return (allValues.topicIds && allValues.topicIds.length) || (allValues.sectorIds && allValues.sectorIds.length) ? undefined : 'lto.generic.no_sector_or_topic_selected';
}

const rules = {
  title: [required()],
  topic: [SectorOrTopicRequired],
  sector: [SectorOrTopicRequired],
  imageurl: [required(), isUrlValid, urlExists],
  image: [required()],
  location: [required()],
  start: [required()],
  end: [required()],
  departments: [required()],
};

export function AgendaFormBasic({ hideSendPushNotification = false, username = '' }) {
  const { guard } = usePermissionGuard();
  const record = useRecordContext();
  const translate = useTranslate();
  const { data, isLoading } = useGetList('ltos');
  const { data: newsData, isLoading: isNewsLoading } = useGetList(
    'news',
    {
      pagination: { page: 1, perPage: 999 },
      sort: { field: 'createdAt', order: 'DESC' },
      filter: { isArchived: false },
      meta: {
        ignoreUserDepartmentFilter: true,
        ignoreUserLTOFilter: true
      }
    },
  );
  const showPush = guard.isAllowed(AgendaResource.permissionResource, 'PushNotifications');
  const ltoMap = useMemo(
    () =>
      data?.reduce((acc, lto) => {
        acc[lto.id] = lto.name;

        return acc;
      }, {}),
    [data],
  );
  if (isLoading || isNewsLoading) return null;

  return (
    <>
      <SideBySideView
        LeftComponent={<TextInput source="title" validate={rules.title} helperText={false} />}
        RightComponent={record ? <SharableLinkButton copiedText={translate('resources.agenda.fields.share_link_copied_to_clipboard')} id={record.id.toString()} type={"agenda"} /> : null}
      />
      <ReferenceArrayInput label="resources.news.fields.sector" source="sectorIds" reference="sectors" perPage={99999} sort={{ field: 'name', order: 'ASC' }}>
        <SelectArrayInput label="resources.news.fields.sector" optionText="name" validate={rules.sector} />
      </ReferenceArrayInput>
      <ReferenceArrayInput label="resources.news.fields.topic" source="topicIds" reference="topics" perPage={99999} sort={{ field: 'name', order: 'ASC' }}>
        <SelectArrayInput label="resources.news.fields.topic" optionText="name" validate={rules.topic} />
      </ReferenceArrayInput>

      <ReferenceArrayInput source="departmentIds" reference="departments" perPage={99999}>
        <GroupedRegionDepartmentSelectArrayInput
          extractGroupLabel={({ ltoId }) => ltoMap[ltoId]}
          groupBy="ltoId"
          sortBy="name"
          label={'resources.news.fields.departments'}
          validate={rules.departments}
          regionsLabel={'resources.news.fields.regions'}
          noRegionsLabel={'resources.news.fields.noRegions'}
        />
      </ReferenceArrayInput>

      <Labeled
        isRequired
        label={"resources.agenda.fields.media"}
      >
        <></>
      </Labeled>

      <BooleanInput source="useMediaUrl" />
      <FormDataConsumer>
        {({ formData, ...rest }) => {
          const displayurl = transformMediaUrlInput(formData.mediaUrl)
          return formData.useMediaUrl ? (
            <>
              <TextInput source="mediaUrl" placeholder='mediaUrl' validate={rules.imageurl} />
              {formData.mediaUrl ? <img style={{ marginBottom: '14px' }} onError={() => urlValid = false} onLoad={() => urlValid = true} src={`${displayurl}${displayurl.includes('?') ? '&' : '?'}width=48&height=48`} width={48} height={48} alt={formData.mediaUrl} /> : null}
            </>
          ) : (
            <ImageInput
              source="media"
              label={false}
              accept="image/png, image/jpeg, image/webp"
              validate={rules.image}
              placeholder={<p>{translate('lto.upload_label')}</p>}
              helperText={'resources.news.imageHelperText'}
            >
              <ServiceImageField source="media" sortable={false} />
            </ImageInput>
          )
        }
        }
      </FormDataConsumer>

      <DateTimePicker source="start" validate={rules.start} label='resources.agenda.fields.start' />
      <DateTimePicker source="end" validate={rules.end} label='resources.agenda.fields.end' />
      <TextInput source="location" validate={rules.location} />

      <RichTextInput
        source="description"
        contentType="json"
        toolbar={
          <RichTextInputToolbar>
            <LevelSelect />
            <HorizontalRuleButton />
            <CustomFormatButtons />
            <ListButtons />
            <ToggleButtonGroup>
              <LinkButtons />
              <LinkContentButton resource={NewsResource.name} />
              <LinkFileButton allowed={['application/msword', 'application/msexcel', 'application/pdf', 'text/plain', 'application/vnd.ms-powerpoint']} />
            </ToggleButtonGroup>
            <ToggleButtonGroup>
              <ImageButton />
              <VideoButton />
            </ToggleButtonGroup>
            <ClearButtons />
          </RichTextInputToolbar>
        }
      />

      <Labeled
        label={"resources.agenda.fields.extra_options"}
      >
        <></>
      </Labeled>
      <BooleanInput source="isPinned" helperText={false} />
      <FormDataConsumer>
        {({ formData }) =>
          formData.isPinned ? (
            <>
              <DateTimePicker source="pinnedFrom" validate={null} label="resources.agenda.fields.pinnedFrom" />
              <DateTimePicker source="pinnedTo" validate={null} label="resources.agenda.fields.pinnedTo" />
            </>
          ) : null
        }
      </FormDataConsumer>
      {
        guard.forceLocal() ?
          <FormGroup className={'ra-input'} row={false}>
            <FormControlLabel
              control={
                <Switch checked={guard.forceLocal()} readOnly />}
              label={<FieldTitle label={translate("resources.agenda.fields.isLocalNews")} />}
            />
          </FormGroup>
          :
          <BooleanInput source="isLocalNews" label={translate("resources.news.fields.isLocalNews")} helperText={false} />
      }
      {!hideSendPushNotification && showPush ? (
        <>
          <BooleanInput source="pushNotification.enabled" helperText={false} />
          <FormDataConsumer>
            {({ formData }) =>
              formData.pushNotification?.enabled ? (
                <>
                  <TextInput source="pushNotification.title" defaultValue="Een nieuw agenda item!" />
                  <TextInput source="pushNotification.description" defaultValue="Er is een nieuw agenda item." />
                </>
              ) : null
            }
          </FormDataConsumer>
        </>
      ) : null}

      <BooleanInput source="allowRegistrations" helperText={false} />
      <BooleanInput source="sendEmailConfirmationOnRegistration" helperText={false} />
      <FormDataConsumer>
        {({ formData }) => {
          return (
            formData.sendEmailConfirmationOnRegistration ?
              <RichTextInput
                source="emailConfirmationText"
                contentType="html"
                toolbar={
                  <RichTextInputToolbar>
                    <HorizontalRuleButton />
                    <CustomFormatButtons />
                    <ListButtons />
                    <ToggleButtonGroup>
                      <LinkButtons />
                      <LinkFileButton allowed={['application/msword', 'application/msexcel', 'application/pdf', 'text/plain', 'application/vnd.ms-powerpoint']} />
                    </ToggleButtonGroup>
                    <ClearButtons />
                  </RichTextInputToolbar>
                }
              />
              : null)
        }}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) => {
          return (<DateTimePicker source="publishedAt" convertFromUTC validate={null} label="resources.agenda.fields.publishAt" />)
        }}
      </FormDataConsumer>
      <BooleanInput source="isPreview" label={translate("resources.agenda.fields.isPreview", { createdBy: username })} helperText={false} />
      <AutocompleteInput multiple label={translate('resources.agenda.fields.newsIds')} source='newsIds' choices={newsData.map(x => ({ id: x.id, name: x.title }))} />
    </>
  );
}

